/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import Drawer from "@atg/icf-react-components/components/Drawer";
import Flex from "@atg/icf-react-components/components/Flex";
import IconButton from "@atg/icf-react-components/components/IconButton";
import Text from "@atg/icf-react-components/components/Text";
import { isNull } from "lodash";
import isEmpty from "lodash/fp/isEmpty";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { getRouteConfig } from "../../../../config/route.config";
import Clickable from "~/components/button/Clickable";
import { Device } from "~/constants";
import ReportRow from "~/features/reportTracker/components/ReportRow";
import {
  selectIsReportTrackerOpen,
  selectReportsData,
  selectShouldRefreshTracker,
  selectSitesByName,
} from "~/features/reportTracker/selectors";
import { reportTrackerActions } from "~/features/reportTracker/slice";
import { loadReportsMetadataAsync } from "~/features/reportTracker/thunks";
import { ReportTypeMappedByStatisticsTab } from "~/features/statistics/constants";
import useDevice from "~/hooks/useDevice";
import useOnClickOutside from "~/hooks/useOnClickOutside";
import { AppDispatch } from "~/state/types";
import reportError from "~/utils/reportError";

const useStyles = createUseStyles(() => ({
  overrides: {
    zIndex: 9999, // Need to set the modal root's zIndex to allow it to show when the table is in fullscreen mode
  },
}));

const ReportTracker: FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const drawerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("reportTracker");
  const location = useLocation();
  const { reportTracker } = getRouteConfig(location.pathname);
  const openState = useSelector(selectIsReportTrackerOpen);
  const reportSelector = useSelector(selectReportsData);
  const reports = useMemo(
    () =>
      reportSelector.filter(
        (reportMetadata) => reportMetadata.params.reportType !== ReportTypeMappedByStatisticsTab.DASHBOARD,
      ),
    [reportSelector],
  );
  const [isMinimised, setIsMinimised] = useState(false);
  const sitesByName = useSelector(selectSitesByName);
  const shouldRefreshTracker = useSelector(selectShouldRefreshTracker);
  const device = useDevice();
  const classes = useStyles();

  const isInStatisticsPage = location.pathname === "/app/statistics";
  useOnClickOutside(drawerRef, () => setIsMinimised(true), openState && !isInStatisticsPage);

  const delay = async (duration: number) => new Promise((resolve) => setTimeout(resolve, duration));

  const getReportsStatus = () => {
    const thunk = loadReportsMetadataAsync();
    dispatch(thunk)
      .unwrap()
      .catch((error) => {
        reportError(error);
      });
  };

  useEffect(() => {
    if (shouldRefreshTracker) {
      getReportsStatus();
      dispatch(reportTrackerActions.setShouldRefreshTracker(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefreshTracker]);

  const poll = async () => {
    // The component gets removed from the DOM on close, so state updates won't come through to the polling loop,
    // therefore the most reliable condition is checking if the drawer component is still in the DOM,
    // because that state is not stored in this component
    while (
      !isNull(document.getElementById("reportTrackerDrawer")) ||
      !isNull(document.getElementById("statisticsFilter"))
    ) {
      getReportsStatus();
      await delay(5000);
    }
  };

  const getWidth = () => {
    if (device === Device.MOBILE) {
      return isMinimised ? 220 : "calc(100% - 48px)";
    }
    return 340;
  };

  useEffect(() => {
    setIsMinimised(!isInStatisticsPage);
  }, [isInStatisticsPage]);

  useEffect(() => {
    if (openState && reports.length === 0) {
      dispatch(reportTrackerActions.setIsReportTrackerOpen(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  return (
    <Drawer
      id="reportTrackerDrawer"
      anchor="bottom"
      right={24}
      open={openState && reportTracker}
      disableOverlay
      width={getWidth()}
      height="auto"
      zIndex={9999}
      overrides={{ root: classes.overrides }}
      ref={drawerRef}
      /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
      onOpen={async () => {
        // The Drawer component locks scrolling for the document body,
        // so we have to set the overflow back from hidden manually to enable scrolling
        document.body.style.overflow = "auto";
        await poll();
      }}
    >
      <Flex
        width="100%"
        direction="row"
        justifyContent="space-between"
        borderWidth="0 0 1 0"
        borderColor="baseContrast015"
        gap={0}
      >
        <Clickable onClick={() => setIsMinimised((prevState) => !prevState)}>
          <Flex padding="$0.5 0 $0.5 $2" width="100%">
            <Text variant="heading_s" letterSpacing={1.5} textTransform="uppercase">
              {t("header")}
            </Text>
          </Flex>
        </Clickable>
        <Flex minWidth={84} padding="$0.5 $0.5 $0.5 0" direction="row" gap={0}>
          <IconButton
            name={isMinimised ? "keyboardArrowUp" : "keyboardArrowDown"}
            as="button"
            onClick={() => setIsMinimised((prevState) => !prevState)}
          />
          <IconButton
            name="close"
            as="button"
            onClick={() => dispatch(reportTrackerActions.setIsReportTrackerOpen(false))}
          />
        </Flex>
      </Flex>
      <Flex gap={0} maxHeight={isMinimised ? 0 : 144} overflow="auto">
        {!isEmpty(reports) &&
          reports.map((report) => <ReportRow key={report.cacheKey} data={report} t={t} sitesByName={sitesByName} />)}
      </Flex>
    </Drawer>
  );
};

export default ReportTracker;
